import {InfiniteHorizontalSlidingContainer} from '#/atoms/infinite-horizontal-sliding-container';
import {
  LandIconInfoMarketTile,
  LandIconInfoMarketTileLoading,
} from '#/components/market-data/land-icon-info-market-tile';
import {useLandMarketInfos} from '#/hooks/query/use-land-market-info';
import {useTrendingLands} from '#/hooks/query/use-trending-lands';
import {ClassName, DefaultChildren} from '#/utils/utils';
import {ExclamationCircleIcon, ExclamationTriangleIcon} from '@heroicons/react/24/solid';
import {ComponentProps, FunctionComponent} from 'react';
import {useTranslation} from 'react-i18next';

type TrendingLandsMarketItemsProps = {
  ItemComponent: FunctionComponent<ComponentProps<typeof LandIconInfoMarketTile>>;
  itemComponentProps: Omit<ComponentProps<typeof LandIconInfoMarketTile>, 'land'>;
};

export const TrendingLandsMarketItems: FunctionComponent<TrendingLandsMarketItemsProps> = ({
  ItemComponent,
  itemComponentProps,
}) => {
  const {t} = useTranslation('translation');

  const {data: lands = [], status: landsStatus, refetch: landsRefetch} = useTrendingLands();
  const {data: landMarketInfos = []} = useLandMarketInfos(lands);

  const isLoading = landsStatus === 'pending';
  const isError = landsStatus === 'error';
  const isEmpty = landsStatus === 'success' && lands.length === 0;
  const isActive = !isLoading && !isError && !isEmpty;

  if (isLoading) {
    return (
      <InfiniteHorizontalSlidingContainer animate={false}>
        {[...Array(20)].map((_, index) => (
          <LandIconInfoMarketTileLoading
            key={index}
            className='pr-6 mr-6 border-r border-gray-300 dark:border-gray-400 h-[60px]'
            {...itemComponentProps}
          />
        ))}
      </InfiniteHorizontalSlidingContainer>
    );
  }

  if (isError) {
    return (
      <NoticeGroupContainer className='px-5 md:px-10 @screen2xl/home:!pl-homeMaxScreen2xl'>
        <NoticeGroupIcon IconComponent={ExclamationCircleIcon} iconColorClassName={'fill-downred stroke-transparent'} />
        <NoticeGroupMessage>{t('pages.home.trending.error.message')}</NoticeGroupMessage>
        <NoticeGroupButton className='bg-deepblue' action={landsRefetch}>
          {t('pages.home.trending.error.ctaReload')}
        </NoticeGroupButton>
      </NoticeGroupContainer>
    );
  }

  if (isEmpty) {
    return (
      <NoticeGroupContainer className='px-5 md:px-10 @screen2xl/home:!pl-homeMaxScreen2xl'>
        <NoticeGroupIcon
          IconComponent={ExclamationTriangleIcon}
          iconColorClassName={'fill-yellowNotice stroke-transparent'}
        />
        <NoticeGroupMessage>{t('pages.home.trending.empty.message')}</NoticeGroupMessage>
        <NoticeGroupButton className='bg-deepblue' action={landsRefetch}>
          {t('pages.home.trending.empty.ctaReload')}
        </NoticeGroupButton>
      </NoticeGroupContainer>
    );
  }

  const landAndMarketInfos = lands.map((land, index) => ({
    land,
    landMarketInfo: landMarketInfos[index],
  }));
  const landsTimeFour = [...landAndMarketInfos, ...landAndMarketInfos, ...landAndMarketInfos, ...landAndMarketInfos];

  return (
    <InfiniteHorizontalSlidingContainer animate={isActive}>
      {landsTimeFour.map(({land, landMarketInfo}, index) => (
        <ItemComponent
          key={index}
          className='pr-6 mr-6 border-r border-gray-300 dark:border-gray-400'
          land={land}
          landMarketInfo={landMarketInfo}
          {...itemComponentProps}
        />
      ))}
    </InfiniteHorizontalSlidingContainer>
  );
};

const NoticeGroupContainer: FunctionComponent<DefaultChildren & ClassName> = ({children, className}) => (
  <div className={`w-full h-[48px] overflow-hidden flex justify-start items-center gap-5 ${className}`}>{children}</div>
);
const NoticeGroupIcon: FunctionComponent<{IconComponent: FunctionComponent<ClassName>; iconColorClassName: string}> = ({
  IconComponent,
  iconColorClassName,
}) => <IconComponent className={`w-10 h-10 ${iconColorClassName}`} />;
const NoticeGroupMessage: FunctionComponent<DefaultChildren> = ({children}) => (
  <span className='grow sm:max-w-fit text-left text-gray-400 font-semibold'>{children}</span>
);
const NoticeGroupButton: FunctionComponent<DefaultChildren & ClassName & {action: Function}> = ({
  children,
  className,
  action,
}) => (
  <button
    className={`px-3 py-2 rounded-xl text-white font-semibold shadow-2xl whitespace-nowrap ${className}`}
    onClick={() => {
      action();
    }}
  >
    {children}
  </button>
);
